import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import {
    addToProposal,
    fetchCenterById,
    updateCallProgress
} from './FirebaseUtils';

export const searchInventory = async (searchData) => {
    try {
        console.log("searchData", searchData);
        // Get current user
        const user = auth.currentUser;
        if (!user) {
            throw new Error('User not authenticated');
        }

        // Construct query based on search criteria
        let q = query(
            collection(db, 'inventories'),
            // Always filter for Private Cabin and Desk
            where('inventoryType', 'in', ['Private Cabin', 'Desk'])
        );
        //Only active inventories
        q = query(q, where('active', '==', true));

        // Add additional filters based on search data
        if (searchData.operatorId) {
            q = query(q, where('operatorId', '==', searchData.operatorId));
        }

        if (searchData.centerId) {
            q = query(q, where('centerId', '==', searchData.centerId));
        }

        if (searchData.city) {
            q = query(q, where('city', '==', searchData.city));
        }

        if (searchData.micromarket) {
            q = query(q, where('micromarket', '==', searchData.micromarket));
        }

        if (searchData.minSize) {
            q = query(q, where('size', '>=', searchData.minSize));
        }

        if (searchData.maxSize) {
            q = query(q, where('size', '<=', searchData.maxSize));
        }

        if (searchData.seatsNeededFrom) {
            q = query(q, where('size', '>=', Number(searchData.seatsNeededFrom)));
        }

        if (searchData.seatsNeededTo) {
            q = query(q, where('size', '<=', Number(searchData.seatsNeededTo)));
        }

        // Get all matching inventory documents
        const querySnapshot = await getDocs(q);
        let inventories = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        // console.log("inventories", inventories);

        // Fetch center data for each inventory
        const results = await Promise.all(
            inventories.map(async (inventory) => {
                try {
                    // Initialize cache if not exists
                    if (!searchInventory.centerCache) {
                        searchInventory.centerCache = new Map();
                    }
                    if (!searchInventory.operatorCache) {
                        searchInventory.operatorCache = new Map();
                    }

                    // Try to get center from cache first
                    let centerData;
                    let centerDoc;
                    if (searchInventory.centerCache.has(inventory.centerId)) {
                        const cachedCenter = searchInventory.centerCache.get(inventory.centerId);
                        centerData = cachedCenter.data;
                        centerDoc = cachedCenter.doc;
                    } else {
                        // Fetch and cache center data if not in cache
                        centerDoc = await getDoc(doc(db, 'centers', inventory.centerId));
                        if (!centerDoc.exists()) {
                            console.warn(`Center not found for inventory ${inventory.id}`);
                            return null;
                        }
                        centerData = centerDoc.data();
                        searchInventory.centerCache.set(inventory.centerId, { doc: centerDoc, data: centerData });
                    }

                    // Try to get operator from cache first
                    let operatorData = {};
                    if (centerData.operatorId) {
                        if (searchInventory.operatorCache.has(centerData.operatorId)) {
                            operatorData = searchInventory.operatorCache.get(centerData.operatorId);
                        } else {
                            // Fetch and cache operator data if not in cache
                            const operatorDoc = await getDoc(doc(db, 'operators', centerData.operatorId));
                            if (operatorDoc.exists()) {
                                operatorData = operatorDoc.data();
                                searchInventory.operatorCache.set(centerData.operatorId, operatorData);
                            } else {
                                console.warn(`Operator not found for center ${inventory.centerId}`);
                            }
                        }
                    }

                    const listPrice = Number(centerData.listPrice);
                    if (searchData.budget && !searchData.budgetFrom && !searchData.budgetTo) {
                        searchData.budgetFrom = searchData.budget * 0.9;
                        searchData.budgetTo = searchData.budget * 1.1;
                    }
                    if (searchData.budgetFrom && listPrice < Number(searchData.budgetFrom)) {
                        console.log("budgetFrom not matching", inventory.id);
                        return null;
                    }
                    if (searchData.budgetTo && listPrice > Number(searchData.budgetTo)) {
                        console.log("budgetTo not matching", inventory.id);
                        return null;
                    }

                    // Return combined data
                    return {
                        ...inventory,
                        center: {
                            id: centerDoc.id,
                            ...centerData
                        },
                        operator: {
                            id: centerData.operatorId,
                            ...operatorData
                        }
                    };
                } catch (error) {
                    console.error(`Error fetching center for inventory ${inventory.id}:`, error);
                    return null;
                }
            })
        );

        // Filter out null results and sort by list price
        const validResults = results
            .filter(result => result !== null)
            .sort((a, b) => Number(a.center.listPrice) - Number(b.center.listPrice))
            .map(result => transformInventoryToOption(result, result.center, result.operator));

        // console.log("validResults", validResults);
        return {
            success: true,
            data: validResults
        };
    } catch (error) {
        console.error('Error searching inventory:', error);
        return {
            success: false,
            error: error.message || 'Error searching inventory'
        };
    }
};

export const transformInventoryToOption = (inventory) => {
    try {
        // console.log('Transforming Inventory:', JSON.stringify(inventory, null, 2));

        const centerId = inventory.centerId;
        const operatorId = inventory.operatorId;

        // Transform inventory photos
        const inventoryPhotos = (inventory.photos || []).map(photo => {
            const { name, type, description, url } = photo;
            return {
                url,
                label: name ? name : type,
                description: description ? description : ''
            };
        });

        // Transform center photos
        const centerPhotos = (inventory.center?.photos || []).map(photo => {
            const { type, name, description, url } = photo;
            return {
                url,
                label: name ? name : type,
                description: description ? description : ''
            };
        });

        // Create and return the option object
        const transformedInventory = {
            id: inventory.id || "",
            lastUpdatedDate: inventory.lastUpdatedDate || null,
            amenities: inventory.center?.amenities || [],
            annualEscalation: inventory.operator.annualEscalation || 0,
            availabilityDate: inventory.availabilityDate || "",
            availableFrom: inventory.availabilityDate || "",
            bikeParkingCost: inventory.center?.bikeParkingCost || 0,
            billableSeats: inventory.size || 0,
            cabinNumber: inventory.cabinNumber || "",
            canSupport247: inventory.center?.canSupport247 || "No",
            carParkingCost: inventory.center?.carParkingCost || 0,
            centerDescription: inventory.center?.description || "",
            centerName: inventory.centerName || "",
            city: inventory.city || "",
            complimentaryBikeParkSlots: inventory.center?.complimentaryBikeParkSlots || 0,
            complimentaryCarParkSlots: inventory.center?.complimentaryCarParkSlots || 0,
            complimentaryMeetingRoomCredits: inventory.center?.complimentaryMeetingRoomCredits || 0,
            customizationCost: inventory.customizationCost || 0,
            floor: inventory.floor || "",
            inventory: inventory.inventoryName || "",
            inventoryDescription: inventory.description || "",
            inventoryType: inventory.inventoryType || "",
            locality: inventory.locality || "",
            location: {
                lat: inventory.center?.latitude || 0,
                landmark: inventory.center?.landmark || "",
                lng: inventory.center?.longitude || 0,
                googleMapsLocation: inventory.center?.googleMapsLocation || "",
                address: inventory.center?.address || ""
            },
            photos: [...inventoryPhotos, ...centerPhotos],
            virtualTours: inventory.center?.virtualTours || [],
            pricePerSeat: inventory.center?.listPrice || 0,
            securityDeposit: (inventory.center?.listPrice * inventory.size * (inventory.operator.deposit || 0)) || 0,
            securityDepositMonths: inventory.operator.deposit || 0,
            lockinPeriod: inventory.operator.lockinPeriod || 0,
            noticePeriod: inventory.operator.noticePeriod || 0,
            totalRentPerMont: (inventory.center?.listPrice * inventory.size) || 0,
            operatorName: inventory.operator.operatorName || "",
            microMarket: inventory.center?.micromarket || "",
        };

        // console.log('Transformed Inventory:', JSON.stringify(transformedInventory, null, 2));

        return transformedInventory;
    } catch (error) {
        console.error('Error transforming inventory data:', error);
        throw error;
    }
};

export const checkInventoryAvailability = async (inventory) => {
    try {
        // Get center data for phone number
        const centerData = await fetchCenterById(inventory.centerId);
        const managerPhone = "+91" + centerData.centerManagerPhone;

        const initiateCallUrl = `https://us-central1-gofloaters-proposals.cloudfunctions.net/initiateCall`;

        // Make HTTP POST request to initiateCall endpoint
        const response = await fetch(initiateCallUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                PhoneNumber: managerPhone,
                centerName: inventory.centerName,
                inventoryType: inventory.inventoryType,
                capacity: inventory.size,
                managerName: centerData.centerManagerName,
                locality: inventory.locality,
                city: inventory.city,
                inventoryId: inventory.id,
                userEmail: auth.currentUser.email,
                requestType: 'InventoryAvailability'
            })
        });

        const result = await response.json();

        // Update call status with call ID
        await updateCallProgress(inventory.id, result.data.callId);

        return {
            success: true,
            message: 'Call initiated successfully',
            callId: result.data.callId
        };

    } catch (error) {
        console.error('Error checking inventory availability:', error);
        throw new Error(error.message || 'Failed to initiate call');
    }
};


// Function to check multiple inventories
export const checkMultipleInventories = async (inventories) => {
    try {
        const results = [];

        // Process inventories sequentially with a delay between each
        for (let i = 0; i < inventories.length; i++) {
            const inventory = inventories[i];

            // Ensure inventory has required fields
            if (!inventory.id || !inventory.centerName) {
                results.push({
                    success: false,
                    error: 'Invalid inventory data',
                    inventoryId: inventory.id || 'unknown'
                });
                continue;
            }

            // Get center ID from inventory document
            const inventoryRef = doc(db, 'inventories', inventory.id);
            const inventoryDoc = await getDoc(inventoryRef);
            if (!inventoryDoc.exists()) {
                results.push({
                    success: false,
                    error: 'Inventory not found',
                    inventoryId: inventory.id
                });
                continue;
            }

            const inventoryData = inventoryDoc.data();
            const enrichedInventory = {
                ...inventory,
                ...inventoryData,
                id: inventory.id // Ensure ID is preserved
            };

            const result = await checkInventoryAvailability(enrichedInventory);
            results.push(result);


        }

        return {
            success: true,
            results,
            message: `Processed ${inventories.length} inventory checks`
        };
    } catch (error) {
        console.error('Error checking multiple inventories:', error);
        return {
            success: false,
            error: error.message || 'Failed to check multiple inventories'
        };
    }
};

export const initiateLeadCall = async (leadData) => {
    try {
        const initiateCallUrl = `https://us-central1-gofloaters-proposals.cloudfunctions.net/initiateCall`;

        // Make HTTP POST request to initiateCall endpoint
        console.log("Lead initiate call request:", leadData);

        const request = {
            phoneNumber: "+91" + leadData.contactPhone,
            inventoryType: leadData.requirement,
            operatorName: leadData.operatorName,
            capacity: leadData.size,
            managerName: leadData.contactName,
            locality: leadData.locality,
            city: leadData.city,
            leadId: leadData.id,
            userEmail: auth.currentUser.email,
        }

        const response = await fetch(initiateCallUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                requestType: "check-inventory-options",
                request: request,
                webhookurl: "https://us-central1-gofloaters-proposals.cloudfunctions.net/getLeadCallStatusData"
            })
        });

        const result = await response.json();

        if (!result.success) {
            throw new Error(result.error || 'Failed to initiate call');
        }

        return {
            success: true,
            message: 'Call initiated successfully',
            callId: result.data.callId
        };

    } catch (error) {
        console.error('Error initiating lead call:', error);
        throw new Error(error.message || 'Failed to initiate call');
    }
};